import type React from 'react';
import { useEffect, useState } from 'react';

import { ANDROID_QR, IOS_QR } from './qrCodes';

interface DownloadSheetProperties {
  isOpen: boolean;
  onClose: () => void;
}

const IOS_URL = 'https://apps.apple.com/in/app/ivalt/id1507945806';
const ANDROID_URL =
  'https://play.google.com/store/apps/details?id=com.abisyscorp.ivalt&hl=en_IN&pli=1';

const PLATFORM_DATA = {
  ios: {
    label: 'iOS',
    url: IOS_URL,
    store: 'App Store',
    icon: (
      <svg width="18" height="18" viewBox="0 0 22.773 22.773" fill="currentColor">
        <path d="M15.769,0c0.053,0,0.106,0,0.162,0c0.13,1.606-0.483,2.806-1.228,3.675c-0.731,0.863-1.732,1.7-3.351,1.573 c-0.108-1.583,0.506-2.694,1.25-3.561C13.292,0.879,14.557,0.16,15.769,0z" />
        <path d="M20.67,16.716c0,0.016,0,0.03,0,0.045c-0.455,1.378-1.104,2.559-1.896,3.655c-0.723,0.995-1.609,2.334-3.191,2.334 c-1.367,0-2.275-0.879-3.676-0.903c-1.482-0.024-2.297,0.735-3.652,0.926c-0.155,0-0.31,0-0.462,0 c-0.995-0.144-1.798-0.932-2.383-1.642c-1.725-2.098-3.058-4.808-3.306-8.276c0-0.34,0-0.679,0-1.019 c0.105-2.482,1.311-4.5,2.914-5.478c0.846-0.52,2.009-0.963,3.304-0.765c0.555,0.086,1.122,0.276,1.619,0.464 c0.471,0.181,1.06,0.502,1.618,0.485c0.378-0.011,0.754-0.208,1.135-0.347c1.116-0.403,2.21-0.865,3.652-0.648 c1.733,0.262,2.963,1.032,3.723,2.22c-1.466,0.933-2.625,2.339-2.427,4.74C17.818,14.688,19.086,15.964,20.67,16.716z" />
      </svg>
    ),
  },
  android: {
    label: 'Android',
    url: ANDROID_URL,
    store: 'Google Play',
    icon: (
      <svg width="18" height="18" viewBox="0 0 1920 1920" fill="currentColor">
        <path
          d="M1306.086 25.225c17.167-25.976 52.29-33.091 78.267-15.698 25.976 17.28 32.978 52.29 15.698 78.266l-85.72 128.637c29.25 18.861 57.372 39.416 83.122 62.907 118.473 108.648 183.752 253.435 183.752 407.71v734.102c0 88.318-76.008 160.034-169.408 160.034h-169.409V1920H1129.45v-338.817H790.633V1920H677.694v-338.817H508.286c-93.4 0-169.408-71.716-169.408-160.034V709.296c0-200.58 107.292-380.266 269.246-488.913L519.58 87.906c-17.393-25.976-10.39-60.987 15.472-78.38 25.863-17.28 60.987-10.277 78.38 15.586l94.304 141.06c59.858-25.862 123.78-44.61 191.883-50.596 109.325-9.6 216.956 7.906 314.083 48.112ZM225.939 734.142v564.694H113V734.142h112.939Zm1581.144 0v564.694h-112.94V734.142h112.94Zm-621.164-282.347c-62.23 0-112.939 50.71-112.939 112.939 0 62.23 50.71 112.939 112.939 112.939 62.23 0 112.939-50.71 112.939-112.94 0-62.228-50.71-112.938-112.939-112.938Zm-451.755 0c-62.23 0-112.94 50.71-112.94 112.939 0 62.23 50.71 112.939 112.94 112.939 62.229 0 112.938-50.71 112.938-112.94 0-62.228-50.71-112.938-112.938-112.938Z"
          fillRule="evenodd"
        />
      </svg>
    ),
  },
};

const QR_IMAGES: Record<string, string> = {
  ios: IOS_QR,
  android: ANDROID_QR,
};

const DownloadSheet: React.FC<DownloadSheetProperties> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<'ios' | 'android'>('ios');
  const [isRendered, setIsRendered] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsRendered(true);
    } else {
      const timer = setTimeout(() => {
        setIsRendered(false);
      }, 350);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  if (!isRendered) return null;

  const platform = PLATFORM_DATA[activeTab];

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black/40 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
        onClick={onClose}
        role="presentation"
      />
      <div
        className={`bg-card border-border fixed right-0 bottom-0 left-0 z-50 max-h-[85vh] overflow-y-auto rounded-t-2xl border-t shadow-2xl transition-transform duration-300 ${isOpen ? 'translate-y-0' : 'translate-y-full'}`}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="border-border flex items-center justify-between border-b px-6 py-4">
          <div className="flex flex-col">
            <h3 className="text-foreground text-base font-bold">Setup iVALT Authentication</h3>
            <span className="text-muted-foreground mt-0.5 text-[11px]">
              Link your mobile device to authorize actions
            </span>
          </div>
          <button
            className="bg-muted hover:bg-accent/10 text-muted-foreground hover:text-foreground flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border-none bg-transparent p-0 text-xl font-medium transition-colors"
            onClick={onClose}
            aria-label="Close"
            type="button"
          >
            &times;
          </button>
        </div>

        {/* Custom Segmented Control Tab Selector */}
        <div className="bg-muted relative mx-6 mt-5 flex rounded-xl p-1">
          <button
            onClick={() => {
              setActiveTab('ios');
            }}
            className={`flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border-none py-2 text-xs font-semibold transition-all duration-200 ${
              activeTab === 'ios'
                ? 'bg-card text-[var(--accent)] shadow-sm'
                : 'text-muted-foreground hover:text-foreground bg-transparent'
            }`}
            type="button"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              {PLATFORM_DATA.ios.icon}
            </span>
            <span>iPhone / iOS</span>
          </button>
          <button
            onClick={() => {
              setActiveTab('android');
            }}
            className={`flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border-none py-2 text-xs font-semibold transition-all duration-200 ${
              activeTab === 'android'
                ? 'bg-card text-[var(--accent)] shadow-sm'
                : 'text-muted-foreground hover:text-foreground bg-transparent'
            }`}
            type="button"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              {PLATFORM_DATA.android.icon}
            </span>
            <span>Android</span>
          </button>
        </div>

        {/* Content Body */}
        <div className="flex flex-col gap-6 p-6">
          <div className="flex flex-col items-center gap-4 text-center">
            {/* Styled QR Code Box */}
            <div className="bg-card border-border group qr-box-animated relative overflow-hidden rounded-2xl border p-3 shadow-md transition-all duration-300 select-none hover:shadow-lg">
              <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-tr from-[var(--accent)] to-orange-400 opacity-0 transition-opacity duration-300 group-hover:opacity-[0.03]" />

              {/* Scan Overlay Laser */}
              <div className="qr-scanner-overlay">
                <div className="qr-scan-line" />
              </div>

              {/* Corner Brackets */}
              <div className="qr-corner top-3 left-3 rounded-tl border-t-2 border-l-2" />
              <div className="qr-corner top-3 right-3 rounded-tr border-t-2 border-r-2" />
              <div className="qr-corner bottom-3 left-3 rounded-bl border-b-2 border-l-2" />
              <div className="qr-corner right-3 bottom-3 rounded-br border-r-2 border-b-2" />

              <img
                src={QR_IMAGES[activeTab]}
                alt={`QR code for ${platform.store}`}
                className="relative z-10 h-40 w-40 rounded-lg object-contain"
              />
            </div>

            <div className="flex flex-col gap-1">
              <p className="text-foreground text-xs font-semibold">Scan with your phone camera</p>
              <p className="text-muted-foreground max-w-[280px] text-[11px]">
                Point your camera at the QR code to open the official {platform.store} listing.
              </p>
            </div>

            <a
              href={platform.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--accent)] py-2.5 text-xs font-semibold text-white no-underline shadow-sm transition-all hover:bg-[var(--accent-dark)] hover:opacity-90 active:scale-[0.98]"
            >
              <span className="flex h-3.5 w-3.5 items-center justify-center">{platform.icon}</span>
              Open {platform.store}
            </a>
          </div>
          {/* App Key Highlights */}
          <div className="border-border flex flex-col gap-3 border-t pt-5 select-none">
            <h4 className="text-muted-foreground text-[10px] font-bold tracking-wider uppercase">
              Why do I need the app?
            </h4>
            <div className="flex flex-col gap-3">
              <div className="flex items-start gap-2.5">
                <div className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-[var(--accent-dim)] text-[10px] font-bold text-[var(--accent)]">
                  ✓
                </div>
                <div className="flex flex-col">
                  <span className="text-foreground text-[11px] font-semibold">
                    1-Tap Biometric Approval
                  </span>
                  <span className="text-muted-foreground text-[10px]">
                    Authorize requests quickly using secure biometrics.
                  </span>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <div className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-[var(--accent-dim)] text-[10px] font-bold text-[var(--accent)]">
                  ✓
                </div>
                <div className="flex flex-col">
                  <span className="text-foreground text-[11px] font-semibold">
                    Secure Identity Keys
                  </span>
                  <span className="text-muted-foreground text-[10px]">
                    Cryptographic private keys remain in the device's secure hardware element.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-border bg-muted/30 border-t px-6 py-3">
          <p className="text-muted-foreground text-center text-[10px]">
            Verification requires the registered iVALT mobile app active on your phone.
          </p>
        </div>
      </div>
    </>
  );
};

export default DownloadSheet;
